<template>
  <div class="security-alert-page">
    <section class="alert-header">
      <v-btn icon class="back-button" @click="$router.push('/home')">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <div>
        <span>Central de segurança</span>
        <h1>Enviar alerta</h1>
      </div>
    </section>

    <section class="panic-panel">
      <v-icon size="42">mdi-shield-alert-outline</v-icon>
      <strong>Precisa de ajuda agora?</strong>
      <p>Escolha o tipo de ocorrência e envie para a equipe responsável pelo seu local.</p>
    </section>

    <section class="type-grid">
      <button
        v-for="type in normalizedTypes"
        :key="type.id"
        :class="['type-card', { active: selectedType && selectedType.id === type.id }]"
        type="button"
        @click="selectedType = type"
      >
        <span class="type-icon">
          <v-icon>{{ type.displayIcon }}</v-icon>
        </span>
        <span>{{ type.displayName }}</span>
      </button>
    </section>

    <section class="form-panel">
      <div class="location-toggle">
        <button :class="{ active: form.location_source === 'home' }" type="button" @click="useHome">
          <v-icon small>mdi-home-map-marker</v-icon>
          Minha casa
        </button>
        <button :class="{ active: form.location_source === 'gps' }" type="button" @click="useGps">
          <v-icon small>mdi-crosshairs-gps</v-icon>
          Local atual
        </button>
        <button :class="{ active: form.location_source === 'manual' }" type="button" @click="useManual">
          <v-icon small>mdi-map-marker-plus-outline</v-icon>
          Outro local
        </button>
      </div>

      <v-textarea
        v-model="form.description"
        label="Observação opcional"
        placeholder="Ex.: veículo parado em frente ao portão..."
        outlined
        rows="3"
        hide-details="auto"
      />

      <div v-if="form.location_source === 'manual'" class="manual-location-panel">
        <div class="manual-address-row">
          <v-text-field
            v-model="manualAddress"
            label="Endereço do alerta"
            placeholder="Digite rua, número ou ponto de referência"
            outlined
            dense
            hide-details="auto"
            @input="onManualAddressInput"
            @keyup.enter="searchAddress"
          />
          <v-btn depressed color="primary" :loading="searchingAddress" :disabled="manualAddress.length < 3" @click="searchAddress">
            Buscar
          </v-btn>
        </div>

        <div v-if="geocodingResults.length" class="address-results">
          <button v-for="result in geocodingResults" :key="result.place_id || result.display_name" type="button" @click="selectAddressResult(result)">
            <v-icon small>mdi-map-marker-outline</v-icon>
            <span>{{ result.display_name }}</span>
          </button>
        </div>

        <div class="manual-map-card">
          <div class="manual-map-header">
            <strong>Selecionar no mapa</strong>
            <span>Toque no mapa ou arraste o pin para ajustar o local.</span>
          </div>
          <LMap
            ref="locationMap"
            class="manual-location-map"
            :zoom="mapZoom"
            :center="mapCenter"
            :options="mapOptions"
            @ready="onMapReady"
            @click="onMapClick"
          >
            <LTileLayer :url="tileProvider.url" :attribution="tileProvider.attribution" />
            <LMarker v-if="hasMapPin" :lat-lng="mapCenter" :draggable="true" @dragend="onMarkerDragEnd" />
          </LMap>
        </div>
      </div>

      <div class="location-preview">
        <v-icon color="primary">mdi-map-marker-outline</v-icon>
        <span>{{ locationText }}</span>
      </div>

      <v-btn block depressed color="primary" class="send-button" :loading="sending" :disabled="!selectedType" @click="confirmSend">
        Enviar alerta
        <v-icon right>mdi-arrow-right</v-icon>
      </v-btn>
    </section>

    <section class="sent-alerts-panel">
      <div class="sent-alerts-header">
        <div>
          <span>Alertas enviados</span>
          <strong>Status atual</strong>
        </div>
        <v-btn icon small :loading="loadingAlerts" @click="loadAlerts">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>

      <div v-if="loadingAlerts && !alerts.length" class="sent-alert-empty">
        Carregando alertas...
      </div>

      <div v-else-if="!alerts.length" class="sent-alert-empty">
        Nenhum alerta enviado por enquanto.
      </div>

      <template v-else>
        <article v-for="alert in alerts" :key="alert.id" class="sent-alert-card">
          <span class="sent-alert-icon">
            <v-icon>{{ iconForAlertType(alert.title || alert.category) }}</v-icon>
          </span>
          <div class="sent-alert-card-header">
            <div>
              <strong>{{ alert.title || alert.category || "Alerta de segurança" }}</strong>
              <span>{{ formatAlertDate(alert.created_at) }}</span>
            </div>
            <span :class="['status-pill', statusClass(alert.status)]">
              {{ statusLabel(alert.status) }}
            </span>
          </div>
          <p v-if="alert.description">{{ alert.description }}</p>
          <div class="sent-alert-meta">
            <span>
              <v-icon small>mdi-map-marker-outline</v-icon>
              {{ alert.location_address || "Local não informado" }}
            </span>
            <span v-if="alert.priority">
              <v-icon small>mdi-alert-circle-outline</v-icon>
              {{ priorityLabel(alert.priority) }}
            </span>
          </div>
        </article>
      </template>
    </section>

    <v-dialog v-model="confirmDialog" max-width="360">
      <v-card class="confirm-card">
        <v-card-title>Confirmar alerta?</v-card-title>
        <v-card-text>
          Este chamado será enviado para a segurança do local.
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="confirmDialog = false">Cancelar</v-btn>
          <v-spacer />
          <v-btn depressed color="primary" :loading="sending" @click="sendAlert">Confirmar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import L from "leaflet";
import { LMap, LMarker, LTileLayer } from "vue2-leaflet";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

export default {
  name: "BoaruaSecurityAlert",
  components: {
    LMap,
    LMarker,
    LTileLayer,
  },
  data() {
    return {
      loadingTypes: false,
      loadingAlerts: false,
      sending: false,
      searchingAddress: false,
      confirmDialog: false,
      types: [],
      alerts: [],
      selectedType: null,
      manualAddress: "",
      geocodingResults: [],
      locationMap: null,
      mapCenter: { lat: -30.0346, lng: -51.2177 },
      mapZoom: 15,
      mapOptions: {
        attributionControl: false,
        zoomControl: true,
      },
      tileProvider: {
        url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
        attribution: "",
      },
      form: {
        location_source: "home",
        latitude: null,
        longitude: null,
        location_accuracy: null,
        location_address: "",
        description: "",
      },
    };
  },
  computed: {
    profile() {
      return JSON.parse(localStorage.getItem("associationProfile") || "{}");
    },
    locationText() {
      if (this.form.location_source === "gps" && this.form.latitude && this.form.longitude) {
        return `Local atual capturado (${Number(this.form.latitude).toFixed(5)}, ${Number(this.form.longitude).toFixed(5)})`;
      }
      if (this.form.location_source === "manual") {
        return this.form.location_address || "Informe o endereço ou selecione o pin no mapa";
      }
      return this.form.location_address || "Endereço residencial vinculado ao cadastro";
    },
    hasMapPin() {
      return Boolean(this.form.latitude && this.form.longitude);
    },
    normalizedTypes() {
      return this.types.map((type) => ({
        ...type,
        displayName: this.alertTypeLabel(type.name || type.category || type.id),
        displayIcon: this.iconForAlertType(type.name || type.category || type.id || type.icon),
      }));
    },
  },
  mounted() {
    this.loadTypes();
    this.loadAlerts();
    this.useHome();
  },
  methods: {
    async loadTypes() {
      this.loadingTypes = true;
      try {
        const response = await axios.get("/security-alerts/types", { params: { tenant_id: this.profile.tenant_id } });
        this.types = response.data.content || [];
        this.selectedType = this.types[0] || null;
      } finally {
        this.loadingTypes = false;
      }
    },
    async loadAlerts() {
      if (!this.profile.tenant_id) return;

      this.loadingAlerts = true;
      try {
        const response = await axios.get("/security-alerts", {
          params: {
            tenant_id: this.profile.tenant_id,
            association_id: this.profile.association_id,
            limit: 10,
          },
        });
        const content = response.data.content || {};
        const rows = Array.isArray(content) ? content : (content.data || []);
        this.alerts = rows.slice(0, 10);
      } catch (error) {
        this.alerts = [];
      } finally {
        this.loadingAlerts = false;
      }
    },
    useHome() {
      const unit = this.profile.units && this.profile.units.length ? this.profile.units[0] : null;
      const coordinates = this.homeCoordinates(unit);
      this.form.location_source = "home";
      this.form.latitude = coordinates ? coordinates.latitude : null;
      this.form.longitude = coordinates ? coordinates.longitude : null;
      this.form.location_accuracy = null;
      this.form.location_address = this.addressFromUnit(unit) || this.addressFromProfile();
      this.syncMapFromForm();
    },
    useGps() {
      this.form.location_source = "gps";
      if (!navigator.geolocation) {
        this.notifyError("Geolocalização não disponível neste dispositivo");
        this.useManual();
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.form.latitude = position.coords.latitude;
          this.form.longitude = position.coords.longitude;
          this.form.location_accuracy = position.coords.accuracy;
          this.syncMapFromForm();
        },
        () => {
          this.notifyError("Não foi possível capturar sua localização. Informe o endereço ou selecione no mapa.");
          this.useManual();
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    },
    useManual() {
      const unit = this.profile.units && this.profile.units.length ? this.profile.units[0] : null;
      const coordinates = this.homeCoordinates(unit);
      this.form.location_source = "manual";
      this.form.location_accuracy = null;
      this.manualAddress = this.form.location_address || this.addressFromProfile() || "";
      if (this.manualAddress && !this.form.location_address) {
        this.form.location_address = this.manualAddress;
      }
      if (!this.isUsableMapCoordinate(this.form.latitude, this.form.longitude) && coordinates) {
        this.form.latitude = coordinates.latitude;
        this.form.longitude = coordinates.longitude;
      }
      this.syncMapFromForm();
      this.refreshMap();
    },
    onManualAddressInput(value) {
      this.form.location_source = "manual";
      this.form.location_address = value;
      if (!String(value || "").trim()) {
        this.geocodingResults = [];
      }
    },
    async searchAddress() {
      const query = String(this.manualAddress || "").trim();
      if (query.length < 3) return;

      this.searchingAddress = true;
      try {
        const response = await axios.get("/geocoding/search", {
          params: {
            q: query,
            limit: 5,
            tenant_id: this.profile.tenant_id,
            center_lat: this.form.latitude || undefined,
            center_lng: this.form.longitude || undefined,
            snap_to_road: true,
          },
        });
        const content = response.data.content || response.data || [];
        this.geocodingResults = Array.isArray(content) ? content : [];
        if (!this.geocodingResults.length) {
          this.notifyError("Nenhum endereço encontrado. Você pode ajustar o pin no mapa.");
        }
      } catch (error) {
        this.notifyError("Não foi possível buscar o endereço");
      } finally {
        this.searchingAddress = false;
      }
    },
    selectAddressResult(result) {
      this.applyManualCoordinates(result.lat, result.lon, result.display_name || this.manualAddress);
      this.geocodingResults = [];
    },
    onMapClick(event) {
      if (!event || !event.latlng) return;
      this.applyManualCoordinates(event.latlng.lat, event.latlng.lng, this.manualAddress || this.form.location_address || "Ponto selecionado no mapa");
    },
    onMarkerDragEnd(event) {
      const latLng = event && event.target && event.target.getLatLng ? event.target.getLatLng() : null;
      if (!latLng) return;
      this.applyManualCoordinates(latLng.lat, latLng.lng, this.manualAddress || this.form.location_address || "Ponto selecionado no mapa");
    },
    applyManualCoordinates(latitude, longitude, address) {
      if (!this.isValidCoordinate(latitude, longitude)) return;
      this.form.location_source = "manual";
      this.form.latitude = Number(latitude);
      this.form.longitude = Number(longitude);
      this.form.location_accuracy = null;
      this.form.location_address = address || "Ponto selecionado no mapa";
      this.manualAddress = this.form.location_address;
      this.mapCenter = { lat: this.form.latitude, lng: this.form.longitude };
      this.refreshMap();
    },
    syncMapFromForm() {
      if (this.isUsableMapCoordinate(this.form.latitude, this.form.longitude)) {
        this.mapCenter = { lat: Number(this.form.latitude), lng: Number(this.form.longitude) };
      }
    },
    onMapReady(map) {
      this.locationMap = map;
      this.refreshMap();
    },
    refreshMap() {
      this.$nextTick(() => {
        const map = this.locationMap || (this.$refs.locationMap && this.$refs.locationMap.mapObject);
        if (map && map.invalidateSize) {
          setTimeout(() => map.invalidateSize({ pan: false }), 120);
        }
      });
    },
    isValidCoordinate(latitude, longitude) {
      const lat = Number(latitude);
      const lng = Number(longitude);
      return Number.isFinite(lat) && Number.isFinite(lng);
    },
    isUsableMapCoordinate(latitude, longitude) {
      if (!this.isValidCoordinate(latitude, longitude)) return false;
      const lat = Number(latitude);
      const lng = Number(longitude);
      return Math.abs(lat) > 0.0001 || Math.abs(lng) > 0.0001;
    },
    homeCoordinates(unit) {
      const sources = [unit || {}, this.profile || {}];
      for (const source of sources) {
        const latitude = source.latitude ?? source.lat ?? source.center_lat ?? source.address_latitude;
        const longitude = source.longitude ?? source.lng ?? source.lon ?? source.center_lng ?? source.address_longitude;
        if (this.isUsableMapCoordinate(latitude, longitude)) {
          return {
            latitude: Number(latitude),
            longitude: Number(longitude),
          };
        }
      }
      return null;
    },
    confirmSend() {
      if (!this.selectedType) {
        this.notifyError("Selecione o tipo de alerta");
        return;
      }
      this.confirmDialog = true;
    },
    async sendAlert() {
      this.sending = true;
      try {
        await axios.post("/security-alerts", {
          tenant_id: this.profile.tenant_id,
          association_id: this.profile.association_id,
          contribution_group_id: this.profile.contribution_group_id,
          member_id: this.profile.id,
          title: this.selectedType.displayName || this.selectedType.name,
          category: this.selectedType.displayName || this.selectedType.name,
          priority: this.selectedType.priority,
          description: this.form.description,
          location_source: this.form.location_source,
          latitude: this.form.latitude,
          longitude: this.form.longitude,
          location_accuracy: this.form.location_accuracy,
          location_address: this.form.location_address,
        });
        this.confirmDialog = false;
        this.form.description = "";
        await this.loadAlerts();
        this.notifySuccess("Alerta enviado para a segurança");
      } catch (error) {
        this.notifyError("Não foi possível enviar o alerta");
      } finally {
        this.sending = false;
      }
    },
    addressFromUnit(unit) {
      if (!unit) return "";
      return [unit.street, unit.number, unit.complement, unit.block, unit.apartment].filter(Boolean).join(", ");
    },
    addressFromProfile() {
      return [this.profile.street, this.profile.number, this.profile.complement].filter(Boolean).join(", ");
    },
    statusLabel(status) {
      const labels = {
        new: "Enviado",
        viewed: "Visualizado",
        in_progress: "Em atendimento",
        on_route: "Equipe a caminho",
        resolved: "Resolvido",
        cancelled: "Cancelado",
        false_alarm: "Falso alarme",
      };
      return labels[status] || "Em análise";
    },
    statusClass(status) {
      const classes = {
        new: "is-new",
        viewed: "is-viewed",
        in_progress: "is-progress",
        on_route: "is-route",
        resolved: "is-resolved",
        cancelled: "is-muted",
        false_alarm: "is-muted",
      };
      return classes[status] || "is-new";
    },
    priorityLabel(priority) {
      const labels = {
        critical: "Crítico",
        high: "Alta prioridade",
        medium: "Prioridade média",
        low: "Baixa prioridade",
      };
      return labels[priority] || priority;
    },
    alertTypeLabel(value) {
      const normalized = this.normalizeText(value);
      const labels = {
        robbery_attempt: "Tentativa de assalto",
        suspicious_activity: "Movimentação suspeita",
        vehicle: "Veículo suspeito",
        access: "Acesso irregular",
        medical: "Emergência médica",
        other: "Outro alerta",
        "tentativa de assalto": "Tentativa de assalto",
        "movimentacao suspeita": "Movimentação suspeita",
        "veiculo suspeito": "Veículo suspeito",
        "acesso irregular": "Acesso irregular",
        "emergencia medica": "Emergência médica",
        "outro alerta": "Outro alerta",
      };
      return labels[normalized] || value || "Alerta";
    },
    iconForAlertType(value) {
      const text = this.normalizeText(value);
      if (text.includes("assalto") || text.includes("roubo")) return "mdi-shield-alert-outline";
      if (text.includes("movimentacao") || text.includes("suspeit")) return "mdi-eye-outline";
      if (text.includes("veiculo") || text.includes("carro")) return "mdi-car";
      if (text.includes("acesso") || text.includes("irregular") || text.includes("portao")) return "mdi-gate";
      if (text.includes("medic") || text.includes("emergencia")) return "mdi-medical-bag";
      if (text.includes("outro")) return "mdi-alert-circle-outline";
      if (String(value || "").startsWith("mdi-")) return value;
      return "mdi-alert-outline";
    },
    normalizeText(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    },
    formatAlertDate(value) {
      if (!value) return "Agora";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "Agora";
      return date.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    notifySuccess(message) {
      if (this.$dialog && this.$dialog.notify) this.$dialog.notify.success(message, { position: "top-right", timeout: 3000 });
    },
    notifyError(message) {
      if (this.$dialog && this.$dialog.notify) this.$dialog.notify.error(message, { position: "top-right", timeout: 4000 });
    },
  },
};
</script>

<style scoped lang="scss">
.security-alert-page {
  min-height: 100%;
  width: 100%;
  max-width: 1080px;
  box-sizing: border-box;
  margin: 0 auto;
  padding: 14px 24px 104px;
  color: #111827;
}

.alert-header {
  min-height: 78px;
  margin: 0 0 12px;
  padding: 12px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  background: transparent;
  border-bottom: 1px solid #edf0ed;

  span {
    color: #267062;
    font-size: 12px;
    font-weight: 800;
  }

  h1 {
    margin: 2px 0 0;
    font-size: 26px;
    line-height: 1;
  }
}

.back-button {
  background: #f5f7f5;
  border: 1px solid #dfe7df;
  border-radius: 12px;
}

.panic-panel {
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: #ffffff;
  background: linear-gradient(135deg, #078b48 0%, #01462c 100%);
  border-radius: 22px;

  .v-icon {
    color: rgba(255, 255, 255, 0.42);
  }

  strong {
    font-size: 24px;
  }

  p {
    max-width: 320px;
    margin: 0;
    color: rgba(255, 255, 255, 0.82);
    font-size: 14px;
  }
}

.type-grid {
  margin: 14px 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.type-card {
  min-height: 86px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  color: #111827;
  background: #ffffff;
  border: 1px solid #e1e8e2;
  border-radius: 16px;
  text-align: left;

  span {
    font-size: 14px;
    font-weight: 850;
    line-height: 1.2;
  }

  &.active {
    border-color: #057a3d;
    box-shadow: 0 10px 28px rgba(5, 122, 61, 0.12);
  }
}

.type-icon {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #edf8f2;
  border-radius: 14px;

  .v-icon {
    color: #057a3d;
    font-size: 24px;
  }
}

.form-panel {
  padding: 16px;
  background: #ffffff;
  border: 1px solid #e1e8e2;
  border-radius: 20px;
}

.location-toggle {
  margin-bottom: 14px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;

  button {
    height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    color: #267062;
    background: #f6faf7;
    border: 1px solid #dfe9e2;
    border-radius: 14px;
    font-weight: 800;

    &.active {
      color: #ffffff;
      background: #057a3d;
      border-color: #057a3d;
    }
  }
}

.manual-location-panel {
  margin-top: 14px;
  display: grid;
  gap: 10px;
}

.manual-address-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: start;

  .v-btn {
    min-width: 92px;
    height: 40px !important;
    border-radius: 12px;
    font-weight: 800;
    letter-spacing: 0;
    text-transform: none;
  }
}

.address-results {
  display: grid;
  gap: 7px;

  button {
    width: 100%;
    padding: 10px 12px;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    color: #24352b;
    background: #f7faf8;
    border: 1px solid #dfe9e2;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.35;
    text-align: left;
  }

  .v-icon {
    color: #057a3d;
    margin-top: 1px;
  }
}

.manual-map-card {
  overflow: hidden;
  border: 1px solid #dfe9e2;
  border-radius: 14px;
  background: #f7faf8;
}

.manual-map-header {
  padding: 11px 12px;
  display: grid;
  gap: 2px;

  strong {
    color: #111827;
    font-size: 14px;
  }

  span {
    color: #66736b;
    font-size: 12px;
    line-height: 1.25;
  }
}

.manual-location-map {
  width: 100%;
  height: 210px;
  border-top: 1px solid #dfe9e2;
}

.manual-location-map::v-deep .leaflet-control-attribution {
  display: none;
}

.location-preview {
  margin: 14px 0;
  padding: 12px;
  display: flex;
  gap: 8px;
  color: #66736b;
  background: #f7faf8;
  border-radius: 14px;
  font-size: 13px;
}

.send-button {
  height: 54px !important;
  border-radius: 16px;
  font-weight: 850;
  letter-spacing: 0;
  text-transform: none;
}

.sent-alerts-panel {
  margin-top: 14px;
  padding: 16px 16px 18px;
  background: #ffffff;
  border: 1px solid #cfded5;
  border-radius: 20px;
}

.sent-alerts-header {
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  div {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  span {
    color: #267062;
    font-size: 12px;
    font-weight: 850;
    text-transform: uppercase;
  }

  strong {
    color: #111827;
    font-size: 20px;
    line-height: 1.1;
  }
}

.sent-alert-empty {
  padding: 16px;
  color: #66736b;
  background: #f7faf8;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 700;
}

.sent-alert-card {
  padding: 13px;
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  column-gap: 12px;
  background: #ffffff;
  border: 1px solid #cfded5;
  border-radius: 16px;

  & + & {
    margin-top: 10px;
  }

  p {
    grid-column: 2;
    margin: 7px 0 0;
    color: #24352b;
    font-size: 13px;
    line-height: 1.35;
  }
}

.sent-alert-icon {
  width: 42px;
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #057a3d;
  background: #edf8f2;
  border-radius: 14px;

  .v-icon {
    color: #057a3d;
    font-size: 23px;
  }
}

.sent-alert-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;

  div {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  strong {
    color: #111827;
    font-size: 15px;
    line-height: 1.2;
  }

  span {
    color: #46544c;
    font-size: 12px;
    font-weight: 700;
  }
}

.status-pill {
  flex: 0 0 auto;
  max-width: 150px;
  padding: 5px 9px;
  color: #ffffff;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 850;
  line-height: 1.1;
  text-align: center;

  &.is-new {
    background: #0b7285;
  }

  &.is-viewed,
  &.is-progress {
    background: #b7791f;
  }

  &.is-route {
    background: #075985;
  }

  &.is-resolved {
    background: #057a3d;
  }

  &.is-muted {
    background: #7b8794;
  }
}

.sent-alert-card-header .status-pill {
  color: #ffffff;
}

.sent-alert-meta {
  grid-column: 2;
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 7px 12px;
  color: #3f5148;
  font-size: 12px;
  font-weight: 800;

  span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }

  .v-icon {
    color: #3f5148;
  }
}

.confirm-card {
  border-radius: 18px;
}

@media (max-width: 719px) {
  .security-alert-page {
    width: 100%;
    padding: 0 16px 18px;
  }

  .alert-header {
    margin: 0 -16px 12px;
    padding: 12px 16px;
    background: #ffffff;
  }

  .panic-panel {
    padding: 20px;
  }

  .type-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .type-card {
    min-height: 90px;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
  }

  .location-toggle {
    grid-template-columns: 1fr;

    button {
      justify-content: flex-start;
      padding: 0 14px;
    }
  }

  .manual-address-row {
    grid-template-columns: 1fr;

    .v-btn {
      width: 100%;
    }
  }

  .manual-location-map {
    height: 190px;
  }

  .sent-alert-card {
    grid-template-columns: 38px minmax(0, 1fr);
  }

  .sent-alert-icon {
    width: 38px;
    height: 38px;
    border-radius: 12px;
  }
}
</style>
